import React, { useState, useContext, Fragment, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {AppContext} from "../App";
import ListingSearch from "../components/listing/ListingSearch";
import CalendarIcon from "../components/utils/icons/CalendarIcon";
import GridViewIcon from "../components/utils/icons/GridViewIcon";
import ListingFooter from "../components/listing/ListingFooter";
import ListingList, { ListingListSkeleton } from "../components/listing/ListingList";
import { listingService } from "../services/ListingService";
import { debounce } from "../utils/functions"
// import Expander from "../components/utils/Expander";
// import Loader from "../components/utils/Loader";
import ListingCalendar from "../components/listing/ListingCalendar";
// import Button from "@showclix-shared/Button"
import Toast from "@showclix-shared/Toast"
import { BackgroundImage, UserPhoto, ListingHeading } from "@showclix-shared/ListingComponents";

const DEFAULT_VIEW_GRID = "1";
const DEFAULT_VIEW_CALENDAR = "2";

const GRID_VIEW = 'grid-view';
const CALENDAR_VIEW = 'calendar';

const defaultViewToTab = (defaultView) => {
    if (defaultView === DEFAULT_VIEW_CALENDAR) {
        return CALENDAR_VIEW;
    }
    return GRID_VIEW;
}
// const PAST_LISTING_LIMIT = 5;

// if there's a requested listing document, force it to the top
const sortListings = (listingsToSort, requestedListingObject) => {
    const sorted = Array.isArray(listingsToSort) ? [...listingsToSort] : [];
    if (requestedListingObject?.listing_id) {
        sorted.sort((a,b) => {
            if (a.listing_id == requestedListingObject.listing_id) {
                return -1;
            }
            if (b.listing_id == requestedListingObject.listing_id) {
                return 1;
            }
            return 0;
        });
    }
    return sorted;
}

const ListingView = (props) => {
    const date = (new URL(location.href))?.searchParams?.get("month")
    const [tab, setTab] = useState(date ? CALENDAR_VIEW : defaultViewToTab(props.defaultView));
    const [filter, setFilter] = useState(undefined);

    const [listings, setFormattedListings] = useState(sortListings(props.listings, props.requestedListingDocument));
    const [listingsLoading, setListingsLoading] = useState(false);

    /**
     * Set the listings keeping in mind that if a requested listing
     * document is passed, it needs to be shown at the top so long as the
     * it's not filtered out of the results by the search bar
     */
    const setListings = providedListings => {
        let toSet = Array.isArray(providedListings) ? [...providedListings] : [];

        // if the user is not searching, and there's a requested
        // listing, force it into the result set
        if (
            Array.isArray(providedListings) &&
            props.requestedListingDocument &&
            !providedListings.find(doc => doc.listing_id == props.requestedListingDocument?.listing_id) &&
            !filter
        ) {
            toSet.push(props.requestedListingDocument);
        }

        setFormattedListings(sortListings(toSet, props.requestedListingDocument));
    };

    // Shouldn’t be shown in Phase 1
    // const [pastListings, setPastListings] = useState([]);
    // const [canLoadMore, setCanLoadMore] = useState(false);
    // const [pastListingLimit, setPastListingLimit] = useState(PAST_LISTING_LIMIT)
    // const [pastListingLoaded, setPastListingLoaded] = useState(false);

    const { apiUrl, sellerShortName, t } = useContext(AppContext);

    // Shouldn’t be shown in Phase 1
    // getPastListings
    const { getUpcomingListings } = useMemo(
        () => listingService(apiUrl),
        [apiUrl]
    );

    const onClickIcon = (icon) => {
        // strip month from url
        if(icon === GRID_VIEW) {
            const url = new URL(location.href);
            window.history.pushState('', '', url.pathname);
        }
        setTab(icon);
    }

    const isIconSelected = (icon) => tab === icon;

    const onChangeFilter = (e) => {
        onClickIcon(GRID_VIEW);
        setFilter(e.target.value);

        debounce(async () => {
            setListingsLoading(true);
            let listingsResponse = [];
            try {
                listingsResponse = await getUpcomingListings(sellerShortName, {
                    name: e.target.value
                });
            } catch (err) {
                Toast.error(t('org_listing__search_error'));
            }
            setListingsLoading(false);
            setListings(listingsResponse?.listings);
        }, 300);
    }

    // Shouldn’t be shown in Phase 1
    // const onClosePastListings = async () => {
    //     setPastListingLimit(PAST_LISTING_LIMIT);
    //     setPastListings([]);
    // }

    // const onOpenPastListings = async () => {
    //     setPastListingLimit(PAST_LISTING_LIMIT);

    //     await resetPastListings(pastListingLimit);
    // }

    // const onClickLoadMore = async () => {
    //     if (pastListingLoaded) {
    //         const limit = pastListingLimit + PAST_LISTING_LIMIT;
    //         setPastListingLimit(limit);

    //         await resetPastListings(limit);
    //     }
    // }

    // const resetPastListings = async (limit) => {
    //     setPastListingLoaded(false);
    //     try {
    //         const { pastListings, hasMore } = await getPastListings(sellerShortName, limit);

    //         setPastListings(pastListings);
    //         setCanLoadMore(hasMore);
    //     } catch (e) {
    //         Toast.error("An error occurred when loading past events. Please, try again.");
    //         setPastListings([]);
    //         setCanLoadMore(false);
    //     } finally {
    //         setPastListingLoaded(true);
    //     }
    // }

    return (
        <>
            <BackgroundImage
                image={props.backgroundImage}
            />
            <main className='org-listing__main'>
                <div className={`org-listing__main__container org-listing__container ${!props.userPhoto ? 'org-listing__main__container--padding-top org-listing__container--no-left' : ''}`}>
                    {props.userPhoto &&
                        <UserPhoto
                            className='listing__user-photo'
                            image={props.userPhoto}
                            name={props.organizationInfo.name}
                        />
                    }
                    <ListingHeading
                        primaryText={props.organizationInfo.name}
                        secondaryText={props.description}
                        className={props.userPhoto ? 'org-listing__user-text--margin-left' : ''}
                    />

                    <div className={`org-listing__upcoming-title ${!props.description && props.userPhoto ? 'org-listing__upcoming-title--extra-margin' : ''}`}>
                        <span className='text-xl-medium'>{t('org_listing__upcoming_events')}</span>
                        <div className='org-listing__upcoming-title-btns-container'>
                            <GridViewIcon
                                selected={isIconSelected(GRID_VIEW)}
                                onClick={() => onClickIcon(GRID_VIEW)}
                            />

                            <CalendarIcon
                                selected={isIconSelected(CALENDAR_VIEW)}
                                onClick={() => onClickIcon(CALENDAR_VIEW)}
                            />
                        </div>
                    </div>

                    <div className='listing-items__tools'>
                        <ListingSearch
                            className='listing-search'
                            onChange={onChangeFilter}
                        />
                        <GridViewIcon
                            selected={isIconSelected(GRID_VIEW)}
                            onClick={() => onClickIcon(GRID_VIEW)}
                        />

                        <CalendarIcon
                            selected={isIconSelected(CALENDAR_VIEW)}
                            onClick={() => onClickIcon(CALENDAR_VIEW)}
                        />
                    </div>
                    { isIconSelected(GRID_VIEW) ? (
                        <Fragment>
                            {listingsLoading
                                ? <ListingListSkeleton/>
                                : <ListingList
                                    listings={listings ?? []}
                                    filter={filter ?? ''}
                                    noItemsText={t('org_listing__no_upcoming_events')}
                                />
                            }
                            {/* Shouldn’t be shown in Phase 1 */}
                            {/* <Expander
                                className='mt-4 mb-4'
                                title={t('org_listing__past_events')}
                                onOpen={onOpenPastListings}
                                onClose={onClosePastListings}
                            >
                                {pastListingLoaded
                                    ? <>
                                        <ListingList noItemsText={t('org_listing__no_past_events_were_found')} listings={pastListings}/>
                                        {canLoadMore &&
                                            <div className='listing__load-more'>
                                                <Button variant='default' onClick={onClickLoadMore}>{t("org_listing__load_more")}</Button>
                                            </div>
                                        }
                                    </>
                                    : <ListingListSkeleton/>
                                }
                            </Expander> */}
                        </Fragment>
                    ) : (
                        <ListingCalendar />
                    )}

                    {props?.organizationInfo &&
                        <ListingFooter
                            organizationInfo={props.organizationInfo}
                            socialMedia={props.socialMedia}
                            isPro={props.isPro}
                        />
                    }
                </div>
            </main>

        </>
    );
}

ListingView.propTypes = {
    listings: PropTypes.array.isRequired,
    userPhoto: PropTypes.string,
    backgroundImage: PropTypes.string,
    description: PropTypes.string,
    organizationInfo: PropTypes.object,
    socialMedia: PropTypes.object,
}

export default ListingView;
